/**
 * Client helpers for weekly AI generation usage + auth headers for metered AI routes.
 */
import { auth } from '../config/firebase'
import { errorFromApi } from '../utils/customerError'
import { getPlan, planIdFromSubscription } from '../data/plansConfig'

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? '/api' : 'http://localhost:3003/api')

export async function getAuthHeaders() {
  const currentUser = auth.currentUser
  if (!currentUser) {
    const err = new Error('Please sign in to generate designs.')
    err.code = 'AUTH_REQUIRED'
    throw err
  }
  let token
  try {
    token = await currentUser.getIdToken()
  } catch (cause) {
    const err = new Error('Your session expired. Please sign in again.')
    err.code = 'AUTH_REQUIRED'
    err.cause = cause
    throw err
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
}

export async function fetchUsage() {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE_URL}/usage`, { headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw errorFromApi(data, res.status, "We couldn't load your usage. Please try again.")
  }
  return data
}

/** Free edits left on a Custom design, read from its server receipt. */
export async function fetchCustomEditStatus(generationId) {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE_URL}/ai/custom-generations/${encodeURIComponent(generationId)}/edits`, {
    headers
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw errorFromApi(data, res.status, "We couldn't load your free edits. Please try again.")
  }
  return data
}

/** Local estimate when API unavailable (never trusted for enforcement). */
export function estimateUsageFromPlan(subscription) {
  const plan = getPlan(planIdFromSubscription(subscription))
  return {
    planId: plan.id,
    planName: plan.name,
    pod: { used: 0, limit: plan.weekly.pod, remaining: plan.weekly.pod },
    custom: { used: 0, limit: plan.weekly.custom, remaining: plan.weekly.custom },
    resetsAt: null
  }
}

export function formatUsageLine(usage) {
  if (!usage?.pod || !usage?.custom) return ''
  if (usage.unlimited) return 'Unlimited this week'
  return `${usage.pod.remaining} graphics / ${usage.custom.remaining} custom left this week`
}

/** True only when the server snapshot says this bucket is used up. Unlimited admins are never blocked. */
export function isBucketExhausted(usage, bucket) {
  if (!usage || usage.unlimited) return false
  const remaining = usage[bucket]?.remaining
  return typeof remaining === 'number' && remaining <= 0
}

export function isLimitReachedError(errOrPayload) {
  if (!errOrPayload) return false
  if (errOrPayload.code === 'LIMIT_REACHED' || errOrPayload.error === 'LIMIT_REACHED') return true
  if (errOrPayload.status === 402) return true
  const msg = String(errOrPayload.message || errOrPayload.error || '')
  return /weekly .* limit reached|LIMIT_REACHED/i.test(msg)
}

export { API_BASE_URL }
